import { Button, Icon, Placeholder, FileExplorer } from '@hai/ui-react';
import {
  HaiDataTableColumnType,
  HaiSortDirection,
  IFileData,
  IHaiColumnSort,
  /* IFileExplorerActionItem,*/
} from '@hai/ui-react/dist/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStateWatch, useWatchObject } from 'src/js/store/use-watch';
import { createListStore, ListSelector } from 'src/js/util/listSelector';
import { AssetModel } from './assets-models';
import { assetsStore, deleteAsset, fetchAssets } from './assets-store';
import { v4 as uuidv4 } from 'uuid';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { constant } from 'src/js/constant';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import systemInfo from 'src/js/data/systemInfo';
import { useNavigate, useParams } from 'react-router-dom';
import useLocalStorageWithState from 'src/js/component/useLocalStorageWithState';
import { useStores } from 'src/js/hook/use-stores';
import { triggerEvent, watchEvent } from 'src/js/events';
import { defaultLocaleInfo } from '@hai/ui-react/dist/components/FileExplorer/FileExplorer-helpers';
import { useLocalStorage } from 'usehooks-ts';
import { GenericDeleteModal } from 'src/js/component/generic-delete-modal';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { createDurationString, DurationFormatterType, formatterForType } from 'src/js/helper/duration-format-helper';
import moment, { Duration } from 'moment';
import { fetchStorage, storageStore } from './storage-store';
import { downloadFile } from 'src/js/helper/csv-helper';

import defaultThumbnail from 'src/images/thumbnail-default.jpg'; // FIXME cache or pre-cache. When system goes offline it tries to fetch it again and appears as not found image.
import { RecordingsBreadcrumb } from './recordings-breadcrumb';
import { RecordingIcon } from 'src/js/component/recording-icon';
import { formatBytesWithUnits } from '../stream/stream-view-model-mapper';
import locationStore from 'src/js/store/location-store';
import { StorageUsageHeader } from './storage-usage-header';
import { doCopyToClipboard } from 'src/js/component/copy-to-clipboard';
import { LazyImg } from 'src/js/component/lazy-img';

interface Props {}

export type AssetPageParameters = {
  id?: string;
};

const getTmpThumbnail = () => {
  return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='; // Transparent image
};

// Formats a date without the year when it falls within the current year,
// otherwise formats it with the year but without hour/minute/second.
const getMinimalisticTime = (date: moment.Moment): string => {
  const isCurrentYear = date.year() === moment().year();
  const formatted = date.format(
    isCurrentYear ? constant.momentFormat.minimalisticCurrentYear : constant.momentFormat.minimalisticPastYear,
  );
  return formatted; // .replace(' AM', 'am').replace(' PM', 'pm');
};

export const RecordingsPage = (_props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useParams<AssetPageParameters>();
  const { sessionStore } = useStores();

  const [loading, setLoading] = useState(true);

  const [assets, setAssets] = useState(assetsStore);
  useWatchObject(assetsStore, setAssets);

  const sortStateDefault: IHaiColumnSort = {
    fieldKey: 'ctimeFormatted',
    order: HaiSortDirection.DESC,
  };
  const sortStateKey = `mx4e:assets:sortState:${sessionStore.username}`;
  const [sortState, setSortState] = useLocalStorage(sortStateKey, sortStateDefault);
  const [sortStateState] = useState(sortState);
  const searchFilterKey = `mx4e:assets:searchFilter:${sessionStore.username}`;
  const [searchFilter, setSearchFilter] = useLocalStorage<string | string[]>(searchFilterKey, undefined);

  const interval = 5000;

  useEffect(() => {
    if (!systemInfo.storage) {
      navigate('/streaming');
      return;
    }
  }, []);

  useAdaptiveFetch(() => systemInfo.storage && fetchStorage(t), interval * 2); // storage before assets so the free space bar loads faster
  useAdaptiveFetch(
    () =>
      fetchAssets(t).then((res) => {
        if (res.ok && loading) {
          setLoading(false);
          triggerEvent('assets-fetched');
        }
        return res;
      }),
    interval,
  );

  const shouldReloadData = () => {
    //fetchVideoInputs();
    fetchAssets(t).then(() => {
      //
    });
  };

  watchEvent('preset-load', () => {
    shouldReloadData();
  });

  const onSortChanged = (k: any, order: any) => {
    // console.log(t, order);
    if (k === 'ctimeFormatted') {
      setSortState({ fieldKey: k, order: order, sortFieldKey: 'ctime' }); // workaround for NKE-2777 / HAIUI-2777
      return;
    }
    setSortState({ fieldKey: k, order: order });
  };

  // TODO improve placeholder
  const placeHolderColumnStructure = [
    { fieldKey: 'name', title: t('general.name') },
    { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
  ];
  const PlaceHolderPage = () => {
    return <Placeholder.Table columnStructure={placeHolderColumnStructure} numRows={3} showTitlebar />;
  };

  const [logoView, setLogoView] = useLocalStorageWithState(
    `${constant.lStorage.logoView}:${sessionStore.username}`,
    'grid',
  );
  const onChangeView = (view: string) => {
    setLogoView(view);
    // listController.reset(); // clear selections. Buggy now because of HAIUI-1525
  };

  const getRawFolderIdFromPathname = (pathname: string): string | undefined => {
    const segments = pathname.split('/');
    if (segments.length < 4 || segments[1] !== 'media' || segments[2] !== 'recordings') {
      return undefined;
    }
    return segments[3] || undefined;
  };

  const [selectedFolder, setSelectedFolder] = useState<string | null>(locationStore.recordings);
  // console.log('selectedFolder', selectedFolder);

  useEffect(() => {
    const syncSelectedFolderFromUrl = () => {
      const rawFolderId = getRawFolderIdFromPathname(window.location.pathname);
      if (rawFolderId == null) {
        locationStore.recordings = null;
        setSelectedFolder(null);
        return;
      }

      const matchingAsset = assetsStore.find(
        (asset) => asset.id === rawFolderId || encodeURIComponent(asset.id) === rawFolderId,
      );
      locationStore.recordings = matchingAsset?.id ?? null;
      setSelectedFolder(matchingAsset?.id ?? null);
    };

    syncSelectedFolderFromUrl();
    window.addEventListener('popstate', syncSelectedFolderFromUrl);

    return () => {
      window.removeEventListener('popstate', syncSelectedFolderFromUrl);
    };
  }, [assets]);

  const getDownloadUrl = (file: AssetModel) => {
    // console.log('file', file);
    if (file.parent != null) {
      return '/apis/asset/' + file.parent.id + '/segment/' + file.id + '?download=true&_=' + Date.now();
    }
    return '/apis/asset/' + file.id + '/download?_=' + Date.now();
  };

  const copySuccessMessage = t('notifications.clipboard.copySuccess');
  const copyFailureMessage = t('notifications.clipboard.copyFailure');
  const downloadSuccessMessage = t('notifications.download.downloadSuccess');
  const downloadBlockedMessage = t('notifications.download.downloadBlocked');
  const copyToClipboard = (_eventKey: any, file: IFileData & AssetModel) => {
    if (file == null || file.id == null) {
      console.warn('Invalid file', file);
      return;
    }
    doCopyToClipboard(`${storageStore.internal.path}/${file.name}`, copySuccessMessage, copyFailureMessage, t);
  };

  const downloadAsset = (file: IFileData & AssetModel): boolean[] => {
    if (file == null || file.id == null) {
      console.warn('Invalid file', file);
      return [];
    }
    if (file.segmented) {
      // download all individual ones
      return file.segments.map((s) => {
        s.parent = file;
        const url = getDownloadUrl(s as unknown as AssetModel);
        return downloadFile(url, file.id);
      });
    }
    const url = getDownloadUrl(file as AssetModel);
    return [downloadFile(url, file.id)];
  };

  const notifyDownloadResults = (results: boolean[]) => {
    if (results.length === 0) {
      return;
    }
    if (results.every(Boolean)) {
      createAndDispatchNotification(
        t('notifications.download.downloadSuccessBody', { count: results.length }),
        NotificationVariant.SUCCESS,
        t,
        {},
        downloadSuccessMessage,
      );
    } else {
      createAndDispatchNotification(downloadBlockedMessage, NotificationVariant.WARNING, t);
    }
  };

  const downloadFileonClick = (_eventKey: any, file: IFileData & AssetModel) => {
    notifyDownloadResults(downloadAsset(file));
  };

  const onDownloadCheckedItems = (_event: any, ids: number[]) => {
    // console.log('download id', ids)
    const results: boolean[] = [];
    ids?.forEach((item: any) => {
      if (rootSegment) {
        const asset = rootSegment.segments?.find((a) => a.id === item);
        results.push(...downloadAsset(asset as any));
      } else {
        const asset = assetsStore.find((a) => a.id === item);
        results.push(...downloadAsset(asset as any));
      }
    });
    notifyDownloadResults(results);
  };

  const openFile = (eventKey: any, file: IFileData | AssetModel) => {
    if (eventKey === 'open-file') {
      return moveIntoFolder(file as AssetModel);
    }
    /*eslint no-console: 0*/
    console.warn('Unsupported action', eventKey);
  };

  let rawContent: Partial<AssetModel>[] = assetsStore;
  let rootSegment: AssetModel = null;
  // console.log('raw', assetsStore);
  if (selectedFolder != null && assetsStore.length > 0) {
    const segment = assetsStore.find((a) => a.id === selectedFolder);
    if (segment) {
      rawContent = segment.segments as unknown as AssetModel[];
      rootSegment = segment;
      if (rawContent && typeof rawContent.map === 'function') {
        rawContent.map((s) => {
          s.parent = segment; // fix each model so it has the parent on it
        });
        // reinit our listController
      } else {
        // Non-segmented asset — navigate to dedicated AssetSingle route
        navigate(`/media/recordings/${encodeURIComponent(selectedFolder)}/view`);
      }
    }
  }

  const [uuid] = useMutable(uuidv4());
  let listController: ListSelector<AssetModel> = null;
  listController = createListStore((rootSegment?.segments as unknown as AssetModel[]) ?? assetsStore, uuid(), 'id');
  const _selectedMatrix = useStateWatch(listController, 'rowMatrix', listController.rowMatrix); // this is to trigger a render when selection changes

  const [selectAll, setSelectAll] = useState(false);
  const handleCheckAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      listController.selectAll();
    } else {
      listController.unselectAll();
    }
  };

  const onBulkDeleteProceed = (checkedFilesInView: AssetModel[]): any => {
    if (checkedFilesInView) {
      let promises = [];

      if (rawContent.length > 0 && rawContent.length === checkedFilesInView.length && rawContent[0].parent) {
        // deleting all segments, instead delete the folder
        promises = [deleteAsset(rawContent[0].parent, t)];
      } else {
        promises = checkedFilesInView.map((itemToDelete) => {
          const item: Partial<AssetModel> = rawContent.find((l) => l.name === itemToDelete.name);
          if (item == null) {
            return { ok: false, error: 'Failed to find asset' }; // should never happen
          }
          return deleteAsset(item, t);
        });
      }

      return Promise.all(promises).then((response) => {
        let returnValue = 'error';
        const errors = response.filter((r) => !r.ok);
        const itemsCount = response.reduce((sum, r) => sum + ((r as any).itemsCount ?? 0), 0);
        const anyError = errors.length > 0;
        if (!anyError) {
          createAndDispatchNotification(
            t('assets.notifications.deleteSuccessMsg', { count: itemsCount ?? response.length }),
            NotificationVariant.SUCCESS,
          );
          returnValue = 'done';
        } else if (errors.length < response.length) {
          // some error and some success
          // error have already been printed individually
          createAndDispatchNotification(
            t('assets.notifications.deleteSuccessWithErrorsMsg', {
              count: response.length - errors.length,
              failures: errors.length,
            }),
            NotificationVariant.WARNING,
          );
        } else if (errors.length === response.length) {
          // error(s)
        }
        listController.reset();
        // force fetch of storage to get fast disk usage
        fetchStorage(t);
        shouldReloadData();

        return returnValue;
      });
    }
    return null;
  };

  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const [deleteLastSegmentShown, showLastSegmentDelete, hideLastSegmentDelete] = useModalControls();
  const onBulkDeleteClicked = (checkedFilesInView: unknown[]): any => {
    if (checkedFilesInView != null && checkedFilesInView.length < listController.list.length) {
      for (let i = 0; i < listController.list.length; i++) {
        const found = checkedFilesInView.find((item) => item === listController.list[i].id);
        if (found == null) {
          // unselect one item not in checkedFilesInView
          listController.unselect(listController.list[i].id);
        }
      }
    } else if (checkedFilesInView != null && checkedFilesInView.length == listController.list.length) {
      // all selected
      showLastSegmentDelete();
      return;
    }

    if (checkedFilesInView) {
      showDelete();
    }
  };

  const checkMultipleHandler = (ids: (number | string)[]) => {
    listController.list.forEach((item) => {
      if (ids.includes(item.id)) {
        listController.select(item.id);
      }
    });
  };

  const moveIntoFolder = (clickedItem: AssetModel) => {
    console.log('folder clicked', clickedItem);
    locationStore.recordings = clickedItem.id;
    setSelectedFolder(clickedItem.id);
    const targetPath = `/media/recordings/${encodeURIComponent(clickedItem.id)}`;
    const replaceUrlOnly = true;
    if (replaceUrlOnly) {
      window.history.pushState(window.history.state, '', targetPath);
      return;
    }

    navigate(targetPath, { replace: true });
  };

  const onClickBack = (evt: React.MouseEvent<HTMLElement>) => {
    evt.stopPropagation();
    evt.preventDefault();
    locationStore.recordings = null;
    setSelectedFolder(null);
    navigate('/media/recordings/', { replace: false });
  };

  const shortFormatter = formatterForType(DurationFormatterType.SHORT, t, '', ' ');
  const filesData: any[] =
    rawContent == null
      ? []
      : rawContent
          .filter((l) => {
            if (!searchFilter || searchFilter.length === 0) {
              return true;
            }
            const filters = Array.isArray(searchFilter) ? searchFilter : [searchFilter];
            return filters.every((f) => {
              //console.log('filter compare', l.name.toLowerCase(), f);
              return l.name.toLowerCase().includes(f?.toLowerCase());
            });
          })
          .map((value, _index: number) => {
            const momentDuration: Duration = moment.duration().add(value.duration, 'seconds');
            const duration = createDurationString(momentDuration, shortFormatter)?.replace('m ', 'm');
            const minimalistic = true;
            const sizeFormatted = formatBytesWithUnits(value.size, 2, minimalistic);

            let creationDateTime = value.ctime;
            if (value.ctime) {
              creationDateTime = getMinimalisticTime(moment.unix(Number(value.ctime)));
            } else {
              const creationDateTimeMatch = value.ctimeFormatted?.match(/^(\d{4}-\d{1,2}-\d{1,2})-(.*)/);
              if (creationDateTimeMatch && creationDateTimeMatch.length >= 3) {
                creationDateTime = `${creationDateTimeMatch[1]} ${creationDateTimeMatch[2]}`;
              }
            }

            // console.log('listController.selectedRows()', listController.selectedRows());

            return {
              selectable: value.status === 'Active' ? false : true,
              fileData: {
                ...value,
                id: value.id,
                name: value.name,
                type: value.segmented ? 'Folder' : undefined,
                typeForList: value.segmented ? `Segmented TS (${value.segments.length})` : 'TS',
                segmentCount: value.segmented ? value.segments.length : 0,
                nameLowerCase: value.name?.toLowerCase(),
                //  inUse: interfaceNames?.length ? interfaceNames : undefined,
                thumbnail:
                  logoView === 'list' ? (
                    value.status === 'Active' ? (
                      <div className="recordingThumbnail">
                        <img className="thumbnail" src={defaultThumbnail} />
                        <div className="recIcon">
                          <RecordingIcon size="60px" className="recording-overlay" />
                        </div>
                        {/* no overlay for list view value.segmented && (
                          <div className="topRightOverlay">
                            <span className="text">{value.segments.length.toString()}</span>
                            <Icon iconname="Assets" className="gray-07-svg" />
                          </div>
                        )*/}
                      </div>
                    ) : (
                      <div className="recordingThumbnail">
                        <LazyImg
                          className="thumbnail"
                          src={getTmpThumbnail()}
                          dataOriginal={
                            value.parent
                              ? `/apis/asset/${value.parent.id}/thumbnail/${value.id}`
                              : `/apis/asset/${value.id}/thumbnail`
                          }
                          style={{ aspectRatio: '16/9' }}
                          retry={new Date().getTime() - value.completedTimestamp < 15000 && 3}
                        />
                        {value.segmented && (
                          <div className="topRightOverlay">
                            <span className="text">{value.segments.length.toString()}</span>
                            <Icon iconname="Assets" className="gray-07-svg" />
                          </div>
                        )}
                      </div>
                    )
                  ) : value.status === 'Active' ? (
                    <div className="recordingThumbnail">
                      <img className="thumbnail" src={defaultThumbnail} />
                      <div className="recIcon">
                        <RecordingIcon size="100px" className="recording-overlay" />
                      </div>
                      {value.segmented && (
                        <div className="topRightOverlay">
                          <span className="text">{value.segments.length.toString()}</span>
                          <Icon iconname="Assets" className="gray-07-svg" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="recordingThumbnail">
                      <LazyImg
                        className="fs-mask"
                        src={getTmpThumbnail()}
                        dataOriginal={
                          value.parent
                            ? `/apis/asset/${value.parent.id}/thumbnail/${value.id}`
                            : `/apis/asset/${value.id}/thumbnail`
                        }
                        style={{ aspectRatio: '16/9' }}
                        retry={new Date().getTime() - value.completedTimestamp < 15000 && 3}
                      />
                      {value.segmented && (
                        <div className="topRightOverlay">
                          <span className="text">{value.segments.length.toString()}</span>
                          <Icon iconname="Assets" className="gray-07-svg" />
                        </div>
                      )}
                    </div>
                  ),

                ctime: value.ctime,
                size: value.size != null ? sizeFormatted.string : undefined,
                sizeBytes: Number(value.size),
                length: duration,
                duration: Number(value.duration),
                ctimeFormatted: creationDateTime,
                //  modifiedDate: creationDateTime,
                checked: listController.selectedRows()?.find((i) => i.id === value.id),
              },
              actions: value.segmented
                ? [
                    {
                      actionType: 'primary',
                      title: t('assets.copyToClipboard'),
                      actionIcon: 'Copy',
                      showOnHover: false,
                      eventKey: 'copyToClipboard',
                      className: 'primary-action-icon',
                      onConfirm: copyToClipboard,
                      onClick: copyToClipboard,
                    },
                    {
                      actionType: 'primary',
                      title: t('general.downloadAll'),
                      actionIcon: 'Download',
                      showOnHover: false,
                      eventKey: 'start-download-all',
                      className: 'primary-action-icon',
                      onConfirm: downloadFileonClick,
                      onClick: downloadFileonClick,
                    },
                    {
                      title: t('assets.viewSegments', { count: value.segments?.length ?? 0 }),
                      actionIcon: 'FolderOpen',
                      showOnHover: false,
                      eventKey: 'open-file',
                      className: 'primary-action-icon',
                      onConfirm: openFile,
                      onClick: openFile,
                    },
                  ]
                : [
                    {
                      actionType: 'primary',
                      title: t('assets.copyToClipboard'),
                      actionIcon: 'Copy',
                      showOnHover: false,
                      eventKey: 'copyToClipboard',
                      className: 'primary-action-icon',
                      onConfirm: copyToClipboard,
                      onClick: copyToClipboard,
                    },
                    {
                      title: t('general.download'),
                      actionIcon: 'Download',
                      showOnHover: false,
                      eventKey: 'start-download',
                      className: 'primary-action-icon',
                      onConfirm: downloadFileonClick,
                      onClick: downloadFileonClick,
                    },
                  ],
              children: [] as unknown[],
            };
          });
  // console.log('filesData', filesData);

  const proceedInlineDeleteSingle = (_eventKey: any, file: AssetModel) => {
    return onBulkDeleteProceed([file]);
  };

  const proceedDeleteSingle = (eventKey: any, file: IFileData | AssetModel) => {
    if (eventKey === 'show-delete') {
      return onBulkDeleteProceed([file as AssetModel]);
    }
    /*eslint no-console: 0*/
    console.warn('Unsupported action', eventKey);
  };

  // this is the kebab menu items
  const actionData /*: IFileExplorerActionItem[]*/ = [
    {
      title: t('general.delete'),
      actionIcon: 'TrashCan',
      showOnHover: false,
      eventKey: 'show-delete',
      className: 'primary-action-icon',
      onConfirm: proceedDeleteSingle,
      inlineAction: {
        confirmationMessage: t('logos.deleteConfirmInline'),
        onConfirm: proceedInlineDeleteSingle,
        iconBackgroundColor: '#ea4645',
      },
    },
  ];

  // These are the action button on top left when items are selected
  const bulkActionComponents = (checkedIds: number[]) => {
    return (
      <Button size="small" onClick={(e: any) => onDownloadCheckedItems(e, checkedIds)}>
        {t('general.download')}
      </Button>
    );
  };

  // FIXME if we are in segment, search into parent.
  const onSelectItem = (_checked: boolean, id: string) => {
    console.log('onSelectedItem', _checked, id);
    // const asset = assets.find((a) => a.id === id);
    const asset = rawContent.find((a) => a.id === id);
    console.log('before select', listController.selectedRows(), asset);
    listController.toggleRow(asset ? asset.id : id);
    console.log('after select', listController.selectedRows());
  };

  const translations = {
    ...defaultLocaleInfo,
    ascendingText: t('logos.fileExplorer.ascendingText'),
    descendingText: t('logos.fileExplorer.descendingText'),
    sortOrderText: t('logos.fileExplorer.sortOrderText'),
    moveModalTitleText: t('logos.fileExplorer.moveModalTitleText'),
    moveModalCurrentLocationText: t('logos.fileExplorer.moveModalCurrentLocationText'),
    moveModalNewLocationText: t('logos.fileExplorer.moveModalNewLocationText'),
    moveModalCancelButtonText: t('logos.fileExplorer.moveModalCancelButtonText'),
    moveModalConfirmButtonText: t('logos.fileExplorer.moveModalConfirmButtonText'),
    deleteFilesDialogCancelText: t('logos.fileExplorer.deleteFilesDialogCancelText'),
    deleteFilesDialogConfirmText: t('logos.fileExplorer.deleteFilesDialogConfirmText'),
    deleteFilesDialogHeadlineText: t('logos.fileExplorer.deleteFilesDialogHeadlineText'),
    deleteFilesDialogBodyText: t('logos.fileExplorer.deleteFilesDialogBodyText'),
    deleteFileHeadlinePendingSingle: t('logos.fileExplorer.deleteFileHeadlinePendingSingle'),
    deleteFileBodyTextPending: t('logos.fileExplorer.deleteFileBodyTextPending'),
    deleteFileHeadlineSuccess: t('logos.fileExplorer.deleteFileHeadlineSuccess'),
    deleteFileHeadlineFail: t('logos.fileExplorer.deleteFileHeadlineFail'),
    deleteFileHeadlinePendingMulti: t('logos.fileExplorer.deleteFileHeadlinePendingMulti'),
    deleteFoldersWithFilesDialogCancelText: t('logos.fileExplorer.deleteFoldersWithFilesDialogCancelText'),
    deleteFoldersWithFilesDialogConfirmText: t('logos.fileExplorer.deleteFoldersWithFilesDialogConfirmText'),
    deleteFoldersWithFilesDialogHeadlineText: t('logos.fileExplorer.deleteFoldersWithFilesDialogHeadlineText'),
    deleteFoldersWithFilesDialogBodyText: t('logos.fileExplorer.deleteFoldersWithFilesDialogBodyText'),
    emptyStateHeadingText:
      assets?.length > 0 && filesData?.length === 0
        ? t('logos.fileExplorer.emptySearchHeadingText')
        : t('assets.fileExplorer.emptyStateHeadingText'),
    emptyStateSubHeadingText: t('assets.fileExplorer.emptyStateSubHeadingText'),
    bulkActionDeleteButtonText: t('logos.fileExplorer.bulkActionDeleteButtonText'),
    bulkActionMoveButtonText: t('logos.fileExplorer.bulkActionMoveButtonText'),
  };

  const selectedFolderAsset = selectedFolder != null ? assetsStore.find((a) => a.id === selectedFolder) : undefined;

  return (
    <>
      <div className="mt-32px" id="recordings-list">
        {!selectedFolderAsset && <StorageUsageHeader storage={storageStore.internal} useThresholdColors={true} />}
        <div>
          <RecordingsBreadcrumb item={selectedFolderAsset} onClickBack={onClickBack} />
        </div>
        <div className="hai-p-0">
          {loading ? (
            <>
              <br />
              <PlaceHolderPage />
            </>
          ) : (
            <div id="recordings-table">
              <FileExplorer
                allowSortNone={false}
                //   disableDoubleClick={true}
                selectable={!sessionStore.isUser()}
                actionItems={!sessionStore.isUser() && actionData}
                checkAll={selectAll}
                currentFolder={filesData}
                dataStructure={[
                  {
                    fieldKey: 'name',
                    title: 'Name',
                    useFsMask: true,
                    type: HaiDataTableColumnType.CUSTOM,
                    sortFieldKey: 'nameLowerCase',
                    secondLevelSortKey: true,
                  },
                  {
                    fieldKey: 'ctimeFormatted',
                    title: t('general.recordedDate'),
                    useAsGridTitle: true,
                    sortFieldKey: 'ctime',
                    className: 'tnum',
                  },
                  {
                    fieldKey: 'length',
                    title: t('general.duration'),
                    useAsGridTitle: true,
                    sortFieldKey: 'duration',
                    alignment: { header: 'right', data: 'right' },
                    className: 'tnum',
                  },
                  {
                    fieldKey: 'size',
                    title: t('general.size'),
                    alignment: { header: 'right', data: 'right' },
                    sortFieldKey: 'sizeBytes',
                    useAsGridTitle: true,
                    className: 'tnum',
                  },
                  {
                    fieldKey: 'typeForList',
                    title: t('general.type'),
                    sortFieldKey: 'segmentCount',
                    alignment: { header: 'right', data: 'right' },
                  },
                  { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
                ]}
                initialSort={sortStateState}
                localeInfo={translations}
                viewType={logoView}
                onThumbnailClick={
                  sessionStore.isUser()
                    ? undefined
                    : (id: any, event: any) => {
                        const item = rawContent.find((a) => a.id === id);
                        if (item == null) {
                          console.error('FAILED TO FIND asset from id', id);
                        }
                        if (item != null) {
                          listController.toggleRow(item.id);
                          if (logoView === 'grid') {
                            event.preventDefault(); // Required for NKE-3184 / HAIUI-2036
                          }
                        }
                      }
                }
                // if we want haiui to handle the delete modal onBulkDelete={onBulkDelete}
                onBulkDeleteClicked={onBulkDeleteClicked}
                additionalBulkActions={bulkActionComponents}
                onCheckAll={handleCheckAll}
                onCheckMultiple={checkMultipleHandler}
                onSelect={onSelectItem}
                onSort={onSortChanged}
                onToggleView={onChangeView}
                moveIntoFolder={moveIntoFolder}
              >
                <FileExplorer.Navigation>
                  <FileExplorer.BreadCrumb />
                  <FileExplorer.ActionGroup
                    allowFiltering={{
                      searchMode: 'simple',
                      filters: typeof searchFilter === 'string' ? searchFilter.split(',') : searchFilter,
                      onChange: (a: string) => {
                        if (a === '') {
                          console.log('ignore empty');
                          return;
                        }
                        return setSearchFilter(a);
                      },
                      maxFilters: 3,
                    }}
                    //  allowMultiple={true} // to have multiple search filters NKE-3205 / HAIUI-2461
                    allowCreateNewFolder={false}
                  >
                    {/*!sessionStore.isUser() && (
                      <Button variant="primary" onClick={showImport}>
                        {t('logos.button.add')}
                      </Button>
                    )*/}
                  </FileExplorer.ActionGroup>
                </FileExplorer.Navigation>
              </FileExplorer>
            </div>
          )}
        </div>
      </div>
      {deleteShown && (
        <GenericDeleteModal
          title="assets.deleteModal.title"
          // if we want transition titleInProgress="assets.deleteModal.titleInProgress"
          itemsToDelete={listController.selectedRows()}
          onCancel={() => {
            hideDelete();
          }}
          onDelete={onBulkDeleteProceed}
          onSuccess={hideDelete}
        />
      )}
      {deleteLastSegmentShown && (
        <GenericDeleteModal
          title="assets.deleteLastSegmentModal.title"
          body={t('assets.deleteLastSegmentModal.subtitle')}
          // if we want transition titleInProgress="assets.deleteLastSegmentModal.titleInProgress"
          itemsToDelete={listController.selectedRows()}
          onCancel={() => {
            hideLastSegmentDelete();
          }}
          onDelete={onBulkDeleteProceed}
          onSuccess={hideLastSegmentDelete}
        />
      )}
      <br />
    </>
  );
};
